import React, {HTMLProps} from 'react'
import styled, {keyframes} from 'styled-components'
import {Link} from 'react-router-dom'
import {darken} from 'polished'
import {anonymizeLink} from '../../utils/anonymizeLink'
import {ArrowLeft, ExternalLink as LinkIconFeather, X} from 'react-feather'

export const ButtonText = styled.button`
  outline: none;
  border: none;
  font-size: inherit;
  padding: 0;
  margin: 0;
  background: none;
  cursor: pointer;

  :hover {
    opacity: 0.7;
  }

  :focus {
    text-decoration: underline;
  }
`

export const Button = styled.button.attrs<{warning: boolean}, {backgroundColor: string}>(({warning, theme}) => ({
  backgroundColor: warning ? theme.dark.red : theme.dark.white,
}))`
  padding: 1rem 2rem 1rem 2rem;
  border-radius: 3rem;
  cursor: pointer;
  user-select: none;
  font-size: 1rem;
  border: none;
  outline: none;
  background-color: ${({backgroundColor}) => backgroundColor};
  color: ${({theme}) => theme.dark.white};
  width: 100%;

  :hover,
  :focus {
    background-color: ${({backgroundColor}) => darken(0.05, backgroundColor)};
  }

  :active {
    background-color: ${({backgroundColor}) => darken(0.1, backgroundColor)};
  }

  :disabled {
    background-color: ${({theme}) => theme.dark.background};
    color: ${({theme}) => theme.dark.blue2};
    cursor: auto;
  }
`

export const CloseIcon = styled(X)<{onClick: () => void}>`
  cursor: pointer;
`

// A button that triggers some onClick result, but looks like a link.
export const LinkStyledButton = styled.button<{disabled?: boolean}>`
  border: none;
  text-decoration: none;
  background: none;

  cursor: ${({disabled}) => (disabled ? 'default' : 'pointer')};
  color: ${({theme, disabled}) => (disabled ? theme.dark.green : theme.dark.white)};
  font-weight: 500;

  :hover {
    text-decoration: ${({disabled}) => (disabled ? null : 'underline')};
  }

  :focus {
    outline: none;
    text-decoration: ${({disabled}) => (disabled ? null : 'underline')};
  }

  :active {
    text-decoration: none;
  }
`

// An internal link from the react-router-dom library that is correctly styled
export const StyledInternalLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  color: ${({theme}) => theme.dark.white};
  font-weight: 500;

  :hover {
    text-decoration: underline;
  }

  :focus {
    outline: none;
    text-decoration: underline;
  }

  :active {
    text-decoration: none;
  }
`

const StyledLink = styled.a`
  text-decoration: none;
  cursor: pointer;
  color: ${({theme}) => theme.dark.white};
  font-weight: 500;
  :focus {
    outline: none;
  }
`
const LinkIconWrapper = styled.a`
  text-decoration: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;

  :hover {
    text-decoration: none;
    opacity: 0.7;
  }

  :focus {
    outline: none;
    text-decoration: none;
  }

  :active {
    text-decoration: none;
  }
`

export const LinkIcon = styled(LinkIconFeather)`
  height: 16px;
  width: 18px;
  margin-left: 10px;
  stroke: ${({theme}) => theme.dark.blue2};
  color: #f2f2f2;
  padding: 8px 8px;
`

function handleClickExternalLink(event: React.MouseEvent<HTMLAnchorElement>) {
  const {target, href} = event.currentTarget

  const anonymizedHref = anonymizeLink(href)

  // don't prevent default, don't redirect if it's a new tab
  if (target === '_blank' || event.ctrlKey || event.metaKey) {
    console.debug('Fired outbound link event', anonymizedHref)
  } else {
    event.preventDefault()
    window.location.href = anonymizedHref
  }
}

export function ExternalLink({
  target = '_blank',
  href,
  rel = 'noopener noreferrer',
  ...rest
}: Omit<HTMLProps<HTMLAnchorElement>, 'as' | 'ref' | 'onClick'> & {href: string}) {
  return <StyledLink target={target} rel={rel} href={href} onClick={handleClickExternalLink} {...rest} />
}

export function ExternalLinkIcon({
  target = '_blank',
  href,
  rel = 'noopener noreferrer',
  ...rest
}: Omit<HTMLProps<HTMLAnchorElement>, 'as' | 'ref' | 'onClick'> & {href: string}) {
  return (
    <LinkIconWrapper target={target} rel={rel} href={href} onClick={handleClickExternalLink} {...rest}>
      <LinkIcon />
    </LinkIconWrapper>
  )
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const Spinner = styled.img`
  animation: 2s ${rotate} linear infinite;
  width: 16px;
  height: 16px;
`

const BackArrowLink = styled(StyledInternalLink)`
  color: ${({theme}) => theme.dark.white};
`
export function BackArrow({to}: {to: string}) {
  return (
    <BackArrowLink to={to}>
      <ArrowLeft />
    </BackArrowLink>
  )
}

export const CustomLightSpinner = styled(Spinner)<{size: string}>`
  height: ${({size}) => size};
  width: ${({size}) => size};
`
